import { computed, inject, Injectable, signal } from '@angular/core';
import { B2BInput } from 'src/app/interfaces/b2b-input';
import { CheckoutService } from 'src/app/pages/main.page/services/checkout.service';
import { SelectedAttributeService } from 'src/app/pages/main.page/services/selected-attribute.service';
import { assert } from 'src/app/utils/assert.util';
import { ConfiguredItemService } from '../configured-item/configured-item.service';
import { CustomerService } from '../customer/customer.service';

@Injectable({
  providedIn: 'root'
})
export class ShareService {
  private checkoutService = inject(CheckoutService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private configuredItemService = inject(ConfiguredItemService);
  private customerService = inject(CustomerService);

  b2bData = signal<B2BInput | null>(null);

  /**
   * The retailer iframe key, resolved and owned by CustomerService.
   *
   * This service used to fetch it itself, which left it reachable only from here — so the item
   * description lookup at add-to-cart read the payload field directly instead and got `undefined`
   * in a B2B session, where the key is not in the payload at all. CustomerService now owns the
   * resolution and CheckoutService starts it as soon as the payload lands.
   */
  retailerIframeID = this.customerService.retailerIframeID;

  constructor() {
    this.checkoutService.subscribeToB2BDataChanges(b2bData => {
      this.b2bData.set(b2bData);
    });
  }

  shouldShowConsumerSiteShare = computed(() => this.checkoutService.shouldShowConsumerSiteShare())
  baseShareURL = computed(() => {
    const url = new URL(window.location.href);
    const shareURL = new URL(this.checkoutService.baseShareURLOverride() ?? `https://${url.host}/share`);
    return shareURL;
  });
  shareLink = computed(() => {
    const shareURL = this.baseShareURL();

    if (this.shouldShowConsumerSiteShare()) {
      const selectedItemSize = this.selectedAttributeService.configuredItem().size;
      assert(selectedItemSize, 'No selected item')
      const shareSKU = this.checkoutService.getHaloSKU(selectedItemSize) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem());
      assert(shareSKU, 'No sku to copy');
      shareURL.pathname += `/${shareSKU}`;
    } else if (!this.checkoutService.baseShareURLOverride()) {
      const shareSKU = this.checkoutService.b2bData.haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem());
      assert(shareSKU, 'No sku to copy');
      shareURL.searchParams.set('sku', shareSKU);

      const configuredItemSize = this.selectedAttributeService.configuredItem().size;
      assert(configuredItemSize, 'No size selected');
      const b2bSize = this.configuredItemService.getB2BFingerSizeObject(configuredItemSize);
      shareURL.searchParams.set('size', b2bSize.size.toString());
      shareURL.searchParams.set('isQuarterSize', b2bSize.isQuarterSize.toString());
      if (this.checkoutService.isRetailerOnly() || this.retailerIframeID()) {
        shareURL.searchParams.set('retailerID', this.checkoutService.retailerIframeID ?? this.retailerIframeID()!);
      }
    }

    if (this.shouldShowConsumerSiteShare() || !this.checkoutService.baseShareURLOverride()) {
      const engravingText = this.selectedAttributeService.configuredItem().engravingText;
      const engravingType = this.selectedAttributeService.configuredItem().engravingType;
      if (engravingType && engravingText) {
        shareURL.searchParams.set('e', engravingType.shortName);
        shareURL.searchParams.set('et', engravingText);
      }

      const po = this.selectedAttributeService.poNumber();
      if (po) {
        shareURL.searchParams.set('po', po);
      }
    }

    return shareURL.href;
  });
}
